import React from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check } from "lucide-react";

const TIERS = [
  {
    id: "basic",
    name: "Basic",
    price: "Free",
    lookup_key: null,
    tagline: "Browse the world of Touche.",
    features: ["Browse practitioner profiles", "Read curated resource previews", "Discreet account"],
  },
  {
    id: "member",
    name: "Member",
    price: "$49",
    period: "/mo",
    lookup_key: "member_monthly",
    tagline: "Full access to the platform.",
    features: ["Everything in Basic", "Book any cuddle therapist", "Full resource library", "Private community access"],
  },
  {
    id: "premium",
    name: "Premium",
    price: "$199",
    period: "/mo",
    lookup_key: "premium_monthly",
    tagline: "Includes founder coaching.",
    popular: true,
    features: ["Everything in Member", "1 founder coaching session /mo", "Priority booking", "Curated shop discount 15%"],
  },
  {
    id: "vip",
    name: "VIP",
    price: "$999",
    period: "/mo",
    lookup_key: "vip_monthly",
    tagline: "Unlimited founder access.",
    features: ["Everything in Premium", "Unlimited coaching sessions", "Concierge booking", "Complimentary quarterly gift", "Direct line to founder"],
  },
];

export default function Membership() {
  const { user } = useAuth();
  const nav = useNavigate();

  const subscribe = async (lookup_key) => {
    if (!lookup_key) { nav("/register"); return; }
    if (!user) { nav("/login", { state: { from: "/membership" } }); return; }
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || "Checkout failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="membership-page">
      <div className="text-center max-w-2xl mx-auto">
        <div className="text-xs uppercase tracking-[0.28em] text-[#C93B3B]">Membership</div>
        <h1 className="font-serif text-5xl lg:text-6xl mt-4 text-[#F7ECEB]">Choose your tier.</h1>
        <p className="text-[#A89896] mt-6 leading-relaxed">Every membership is private, monthly, and cancellable anytime.</p>
      </div>

      <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {TIERS.map((t) => (
          <div
            key={t.id}
            data-testid={`pricing-tier-${t.id}`}
            className={`relative tt-card p-8 flex flex-col ${
              t.popular ? "ring-2 ring-[#E58B5E] shadow-[0_16px_48px_rgba(204,143,120,0.16)]" : ""
            }`}
          >
            {t.popular && (
              <div className="absolute -top-3 left-8 text-xs uppercase tracking-[0.2em] bg-[#E58B5E] text-white px-3 py-1 rounded-full">
                Most popular
              </div>
            )}
            <div className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">{t.name}</div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-serif text-5xl text-[#F7ECEB]">{t.price}</span>
              {t.period && <span className="text-[#A89896]">{t.period}</span>}
            </div>
            <p className="text-sm text-[#A89896] mt-3">{t.tagline}</p>
            <ul className="mt-8 space-y-3 flex-1">
              {t.features.map((f) => (
                <li key={f} className="text-sm text-[#F7ECEB] flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#C93B3B] mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => subscribe(t.lookup_key)}
              data-testid={`subscribe-${t.id}`}
              className={`mt-8 w-full ${t.popular ? "btn-secondary" : t.id === "basic" ? "btn-ghost" : "btn-primary"}`}
            >
              {t.id === "basic" ? (user ? "Current tier" : "Start free") : (user?.membership_tier === t.id ? "Current tier" : "Choose " + t.name)}
            </button>
          </div>
        ))}
      </div>

      <div className="mt-16 max-w-3xl mx-auto text-center text-sm text-[#A89896]">
        All payments processed securely by Stripe. Cancel anytime from your dashboard.
      </div>
    </div>
  );
}
